import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, RSD } from "../../lib/api";
import { printThermalSlip, reprintOrder } from "../../lib/printSlip";
import { useShift } from "../../hooks/useShift";
import ShiftControl from "../../components/shift/ShiftControl";
import { toast } from "sonner";
import { Plus, Minus, Trash2, X, Send, Banknote, CreditCard, Pencil, Printer, CalendarClock } from "lucide-react";
import { FeatureLocked } from "../../components/FeatureLocked";
import { canPrintSlips } from "../../lib/deviceClass";
import { localMinutesOfDay, shiftHHMM } from "../../lib/restaurantClock";

const newKey = () => `io-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const PREP_PRESETS = [5, 10, 15, 20];
const PREP_MIN = 1;
const PREP_MAX = 60;

// Fast counter entry: tap a preset, then fine-tune by exactly 1 minute. No modal, no snap-back.
function PrepTimeControl({ minutes, onChange }) {
  const step = (d) => onChange(Math.min(PREP_MAX, Math.max(PREP_MIN, minutes + d)));
  return (
    <div data-testid="io-prep-time" className="flex items-center gap-2 flex-wrap">
      <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Vreme pripreme</p>
      <div className="flex items-center gap-1">
        {PREP_PRESETS.map((m) => (
          <button key={m} data-testid={`io-prep-preset-${m}`} onClick={() => onChange(m)}
            className={`w-12 h-12 rounded-xl text-base font-display font-black border-2 active:scale-95 transition-transform ${
              minutes === m ? "border-brand-red bg-brand-red hover:bg-brand-redHover text-white" : "border-slate-200 bg-white text-slate-600"}`}>
            {m}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1 rounded-xl bg-white border-2 border-slate-200 p-1">
        <button data-testid="io-prep-minus" onClick={() => step(-1)} disabled={minutes <= PREP_MIN}
          className="w-11 h-10 rounded-lg bg-slate-100 flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform">
          <Minus size={18} />
        </button>
        <span data-testid="io-prep-value" className="min-w-[72px] text-center font-display font-black text-lg">{minutes} min</span>
        <button data-testid="io-prep-plus" onClick={() => step(1)} disabled={minutes >= PREP_MAX}
          className="w-11 h-10 rounded-lg bg-slate-100 flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform">
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
}

// Compact same-day scheduling for counter orders. The preparation duration comes from the shared
// VREME PRIPREME control, so there is only ever one prep selector on this page.
function ScheduleControl({ scheduled, time, onToggle, onTime, prepMinutes }) {
  const [hh, mm] = /^\d{2}:\d{2}$/.test(time || "") ? time.split(":") : ["", ""];
  const prepStart = React.useMemo(() => {
    if (!scheduled || !/^\d{2}:\d{2}$/.test(time || "")) return null;
    return shiftHHMM(time, -prepMinutes);
  }, [scheduled, time, prepMinutes]);
  return (
    <div data-testid="io-schedule" className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1 rounded-xl bg-white border-2 border-slate-200 p-1">
        <button data-testid="io-schedule-now" onClick={() => onToggle(false)}
          aria-pressed={!scheduled}
          className={`h-10 px-3 rounded-lg text-sm font-black ${!scheduled ? "bg-brand-red text-white" : "text-slate-500"}`}>Odmah</button>
        <button data-testid="io-schedule-later" onClick={() => onToggle(true)}
          aria-pressed={scheduled}
          className={`h-10 px-3 rounded-lg text-sm font-black flex items-center gap-1 ${scheduled ? "bg-brand-red text-white" : "text-slate-500"}`}>
          <CalendarClock size={15} />Zakaži
        </button>
      </div>
      {scheduled && (
        <div className="flex items-center gap-2">
          {/* Native selects, not <input type="time">: those render in the BROWSER locale (12h AM/PM
              on en-US devices). Ponesi is restaurant-local 24h, so hours/minutes are explicit. */}
          <div className="flex items-center gap-1 rounded-xl border-2 border-slate-200 bg-white px-2 h-12">
            <select data-testid="io-schedule-hour" aria-label="Sat preuzimanja" value={hh}
              onChange={(e) => onTime(`${e.target.value}:${mm}`)}
              className="h-10 bg-transparent font-display font-black text-lg outline-none">
              {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <span className="font-display font-black text-lg">:</span>
            <select data-testid="io-schedule-minute" aria-label="Minut preuzimanja" value={mm}
              onChange={(e) => onTime(`${hh}:${e.target.value}`)}
              className="h-10 bg-transparent font-display font-black text-lg outline-none">
              {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          {prepStart && (
            <span data-testid="io-prep-start" className="text-[11px] font-bold text-slate-500">
              Početak pripreme: <b className="text-slate-700">{prepStart}</b>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const COLORS = {  RED: "text-brand-red", GREEN: "text-brand-green", ORANGE: "text-brand-orange",
  BLUE: "text-blue-600", PURPLE: "text-purple-600", DEFAULT: "text-slate-600",
};

function QuickConfig({ product, groups, initial, onCancel, onAdd }) {
  const [sel, setSel] = useState(initial?.ids || []);
  const [person, setPerson] = useState(initial?.person || "");
  const [note, setNote] = useState(initial?.note || "");
  const editing = !!initial;
  const toggle = (g, o) => {
    setSel((prev) => {
      const ids = g.options.map((x) => x.id);
      if (g.type === "single") return [...prev.filter((id) => !ids.includes(id)), o.id];
      if (prev.includes(o.id)) return prev.filter((id) => id !== o.id);
      const max = Number(g.max_selection || 0);
      const chosen = prev.filter((id) => ids.includes(id));
      if (max && chosen.length >= max) return prev;
      return [...prev, o.id];
    });
  };
  const unit = useMemo(() => {
    const extra = groups.flatMap((g) => g.options).filter((o) => sel.includes(o.id)).reduce((s, o) => s + (o.price || 0), 0);
    return product.price + extra;
  }, [sel, groups, product.price]);
  const missing = groups.filter((g) => g.required && !g.options.some((o) => sel.includes(o.id)));

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="io-config">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            {editing && <p className="text-xs font-black uppercase tracking-wider text-brand-red">Izmeni stavku</p>}
            <h3 className="font-display font-black text-xl">{product.name}</h3>
          </div>
          <button data-testid="io-config-close" onClick={onCancel} className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-slate-100"><X size={20} /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-5">
          {groups.map((g) => (
            <div key={g.id} data-testid={`io-group-${g.id}`}>
              <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">
                {g.name}{g.required ? " *" : ""}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {g.options.filter((o) => o.available !== false).map((o) => (
                  <button key={o.id} data-testid={`io-opt-${o.id}`} onClick={() => toggle(g, o)}
                    className={`rounded-xl px-3 py-4 text-sm font-bold border-2 text-left leading-tight ${sel.includes(o.id) ? "border-brand-red bg-brand-red/5 text-brand-red" : "border-slate-200"}`}>
                    {o.name}{o.price ? <span className="block text-xs font-semibold text-slate-400">+{RSD(o.price)}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs font-black uppercase tracking-wider text-slate-400">Za?</span>
              <input data-testid="io-person" value={person} onChange={(e) => setPerson(e.target.value)} placeholder="Marko"
                className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base" /></label>
            <label className="block"><span className="text-xs font-black uppercase tracking-wider text-slate-400">Napomena</span>
              <input data-testid="io-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Bez luka"
                className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base" /></label>
          </div>
        </div>
        <div className="px-5 py-4 border-t flex gap-2">
          <button data-testid="io-config-add" disabled={missing.length > 0}
            onClick={() => onAdd({ product, ids: sel, person: person.trim(), note: note.trim(), unit })}
            className="flex-1 rounded-2xl bg-brand-red hover:bg-brand-redHover text-white py-4 font-display font-black text-lg disabled:opacity-40">
            {missing.length ? `Izaberite: ${missing[0].name}` : `${editing ? "SAČUVAJ IZMENE" : "DODAJ"} · ${RSD(unit)}`}
          </button>
          {editing && (
            <button data-testid="io-config-cancel" onClick={onCancel}
              className="px-5 rounded-2xl bg-slate-100 font-bold">Otkaži</button>
          )}
        </div>
      </div>
    </div>
  );
}

function QuickConfigManager({ rid, products, quick, onClose, onChanged }) {
  const [form, setForm] = useState(null); // {id?, product_id, option_ids, quick_price}
  const [busy, setBusy] = useState(false);
  const product = products.find((p) => p.id === form?.product_id);
  const groups = product?.modifier_groups || [];

  const save = async () => {
    if (!form?.product_id || !form?.quick_price) { toast.error("Izaberite proizvod i cenu"); return; }
    setBusy(true);
    try {
      const body = { product_id: form.product_id, option_ids: form.option_ids, quick_price: Number(form.quick_price) };
      if (form.id) await api.put(`/restaurant/${rid}/quick-selections/${form.id}`, body);
      else await api.post(`/restaurant/${rid}/quick-selections`, body);
      setForm(null); await onChanged(); toast.success("Sačuvano");
    } catch (e) { toast.error(e?.response?.data?.detail || "Greška"); }
    finally { setBusy(false); }
  };
  const remove = async (q) => {
    if (!window.confirm("Da li ste sigurni da želite da uklonite ovaj brzi izbor?")) return;
    await api.delete(`/restaurant/${rid}/quick-selections/${q.id}`); await onChanged();
  };
  const move = async (i, d) => {
    const ids = quick.map((q) => q.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.post(`/restaurant/${rid}/quick-selections/reorder`, { ids }); await onChanged();
  };
  const toggleOpt = (g, o) => setForm((f) => {
    const ids = g.type === "single"
      ? [...f.option_ids.filter((id) => !g.options.some((x) => x.id === id)), o.id]
      : (f.option_ids.includes(o.id) ? f.option_ids.filter((id) => id !== o.id) : [...f.option_ids, o.id]);
    return { ...f, option_ids: ids };
  });

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="quick-config-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-display font-black text-xl">Podešavanje brzog izbora</h3>
          <button data-testid="quick-config-close" onClick={onClose} className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-slate-100"><X size={20} /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {quick.map((q, i) => (
            <div key={q.id} data-testid={`quick-row-${q.id}`} className="flex items-center gap-2 border-b pb-2">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">{q.product_name} · {RSD(q.quick_price)}</p>
                <p className="text-xs text-slate-500 truncate">{(q.option_names || []).join(" · ") || "—"}</p>
                {q.needs_update && <p className="text-xs font-bold text-brand-orange">Potrebno ažurirati brzi izbor</p>}
              </div>
              <button data-testid={`quick-up-${q.id}`} onClick={() => move(i, -1)} className="w-9 h-9 rounded-lg bg-slate-100">↑</button>
              <button data-testid={`quick-down-${q.id}`} onClick={() => move(i, 1)} className="w-9 h-9 rounded-lg bg-slate-100">↓</button>
              <button data-testid={`quick-edit-${q.id}`} onClick={() => setForm({ id: q.id, product_id: q.product_id, option_ids: q.option_ids || [], quick_price: q.quick_price })}
                className="px-3 h-9 rounded-lg bg-slate-100 text-sm font-bold">Izmeni</button>
              <button data-testid={`quick-delete-${q.id}`} onClick={() => remove(q)} className="w-9 h-9 rounded-lg bg-brand-red/10 text-brand-red flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          ))}

          {!form && (quick.length >= 6
            ? <p data-testid="quick-limit" className="text-sm font-bold text-slate-500">Maksimalno 6 brzih izbora.</p>
            : <button data-testid="quick-add" onClick={() => setForm({ product_id: "", option_ids: [], quick_price: "" })}
                className="w-full rounded-xl border-2 border-dashed border-slate-300 py-3 font-bold text-slate-600">+ Dodaj brzi izbor</button>)}

          {form && (
            <div className="space-y-3 border-t pt-3">
              <label className="block"><span className="text-xs font-black uppercase tracking-wider text-slate-400">Naziv proizvoda</span>
                <select data-testid="quick-product" value={form.product_id}
                  onChange={(e) => setForm({ ...form, product_id: e.target.value, option_ids: [] })}
                  className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base">
                  <option value="">Izaberite proizvod</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></label>
              {groups.map((g) => (
                <div key={g.id} data-testid={`quick-group-${g.id}`}>
                  <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">{g.name}{g.required ? " *" : ""}</p>
                  <div className="flex flex-wrap gap-2">
                    {g.options.map((o) => (
                      <button key={o.id} data-testid={`quick-opt-${o.id}`} onClick={() => toggleOpt(g, o)}
                        className={`rounded-lg px-3 py-2 text-sm font-bold border-2 ${form.option_ids.includes(o.id) ? "border-brand-red bg-brand-red/5 text-brand-red" : "border-slate-200"}`}>
                        {o.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <label className="block"><span className="text-xs font-black uppercase tracking-wider text-slate-400">Cena (RSD)</span>
                <input data-testid="quick-price" type="number" value={form.quick_price}
                  onChange={(e) => setForm({ ...form, quick_price: e.target.value })}
                  className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base" /></label>
              <p className="text-[11px] text-slate-400">Cena važi samo za Ponesi Lokal i ne menja cenu u meniju za kupce.</p>
              <div className="flex gap-2">
                <button data-testid="quick-save" disabled={busy} onClick={save} className="flex-1 rounded-xl bg-brand-red hover:bg-brand-redHover text-white py-3 font-bold disabled:opacity-50">Sačuvaj</button>
                <button onClick={() => setForm(null)} className="px-4 rounded-xl bg-slate-100 font-bold">Otkaži</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function InternalOrder() {
  const { restaurant, role, features } = useOutletContext();
  const vistaLocked = features?.VISTA === false;
  const shiftState = useShift(restaurant.id);
  const canConfigure = ["OWNER", "MANAGER", "PLATFORM_ADMIN"].includes((role || "").toUpperCase());
  const [menu, setMenu] = useState({ categories: [], products: [] });
  const [cat, setCat] = useState(null);
  const [config, setConfig] = useState(null);
  const [lines, setLines] = useState([]);
  const [payment, setPayment] = useState(null);
  const [sending, setSending] = useState(false);
  const [last, setLast] = useState(null);
  const [payErr, setPayErr] = useState(false);
  const [note, setNote] = useState("");
  const [scheduled, setScheduled] = useState(false);
  const [schedTime, setSchedTime] = useState("");
  const clientId = useRef(newKey());
  const defaultPrep = Math.min(PREP_MAX, Math.max(PREP_MIN, Number(restaurant.prep_time) || 15));
  const [prepMinutes, setPrepMinutes] = useState(defaultPrep);
  useEffect(() => { setPrepMinutes(defaultPrep); }, [defaultPrep]);

  const [quick, setQuick] = useState([]);
  const [quickCfg, setQuickCfg] = useState(false);

  const loadQuick = () => api.get(`/restaurant/${restaurant.id}/quick-selections`)
    .then(({ data }) => setQuick(data)).catch(() => setQuick([]));
  useEffect(() => { loadQuick(); /* eslint-disable-next-line */ }, [restaurant.id]);

  useEffect(() => {
    api.get(`/restaurants/${restaurant.slug}/menu`).then(({ data }) => {
      setMenu(data);
      setCat((data.categories || [])[0]?.id || null);
    }).catch(() => toast.error("Meni nije moguće učitati"));
  }, [restaurant.slug]);

  // Vista payment configuration is independent of the customer-facing Ponesi setting; "NONE"
  // means the restaurant does not track payment on counter orders, so the step disappears.
  const paySetting = (restaurant.vista_payment_method || restaurant.payment_method || "BOTH").toUpperCase();
  const allowCash = paySetting === "CASH" || paySetting === "BOTH";
  const allowCard = paySetting === "CARD" || paySetting === "BOTH";
  const payRequired = allowCash || allowCard;

  // Products whose category is outside its time window stay VISIBLE but disabled, so staff
  // understands why they cannot be ordered right now.
  const closedCats = new Set((menu.categories || []).filter((c) => c.schedule_status?.open_now === false).map((c) => c.id));
  const available = (menu.products || []).filter((p) => p.available !== false || closedCats.has(p.category_id));
  const shown = available.filter((p) => p.category_id === cat);
  const total = lines.reduce((s, l) => s + l.unit * l.qty, 0);

  const addQuick = (q) => {
    if (!q.available) { toast.error(q.needs_update ? "Potrebno ažurirati brzi izbor" : "Nedostupno u ovom terminu"); return; }
    setLines((prev) => {
      const sig = `qs-${q.id}`;
      const found = prev.find((l) => l.sig === sig);
      if (found) return prev.map((l) => (l.sig === sig ? { ...l, qty: l.qty + 1 } : l));
      return [...prev, { key: newKey(), sig, quick_selection_id: q.id, product_id: q.product_id, name: q.product_name,
        ids: q.option_ids, person: "", note: "", unit: q.quick_price, qty: 1,
        names: (q.option_names || []).map((n) => ({ name: n, color: "DEFAULT" })) }];
    });
  };

  const openProduct = (p) => {
    const groups = [...(p.modifier_groups || [])];
    if (!groups.length) { addLine({ product: p, ids: [], person: "", note: "", unit: p.price }); return; }
    setConfig({ product: p, groups });
  };
  const editLine = (l) => {
    const product = available.find((p) => p.id === l.product_id);
    if (!product) { toast.error("Proizvod nije pronađen u meniju"); return; }
    setConfig({ product, groups: [...(product.modifier_groups || [])],
      initial: { key: l.key, ids: l.ids || [], person: l.person || "", note: l.note || "" } });
  };
  const addLine = ({ product, ids, person, note, unit }) => {
    const editKey = config?.initial?.key;
    setConfig(null);
    const names = (product.modifier_groups || []).flatMap((g) => g.options.filter((o) => ids.includes(o.id))
      .map((o) => ({ name: o.name, color: g.kitchen_color || "DEFAULT" })));
    const sig = `${product.id}|${[...ids].sort().join(",")}|${person}|${note}`;
    if (editKey) {
      setLines((prev) => prev.map((l) => (l.key !== editKey ? l
        : { ...l, sig, quick_selection_id: undefined, product_id: product.id, name: product.name, ids, person, note, unit, names })));
      return;
    }
    setLines((prev) => {
      const found = prev.find((l) => l.sig === sig);
      if (found) return prev.map((l) => (l.sig === sig ? { ...l, qty: l.qty + 1 } : l));
      return [...prev, { key: newKey(), sig, product_id: product.id, name: product.name, ids, person, note, unit, qty: 1, names }];
    });
  };
  const setQty = (key, d) => setLines((prev) => prev.flatMap((l) => (l.key !== key ? [l] : (l.qty + d < 1 ? [] : [{ ...l, qty: l.qty + d }]))));
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));
  const clearDraft = () => {
    if (lines.length && !window.confirm("Obrisati porudžbinu?")) return;
    setLines([]); setPayment(null); setPayErr(false); setNote("");
    setScheduled(false); setSchedTime("");
  };

  const doPrint = async (order) => {
    if (!canPrintSlips()) return false;        // phones/tablets never open a print dialog
    try {
      await printThermalSlip(order);
      return true;
    } catch {
      toast.error("Porudžbina je poslata u pripremu, ali slip nije odštampan.");
      return false;
    }
  };

  const submit = async () => {
    if (!lines.length || sending) return;
    if (!shiftState.shift) { toast.error("Pokrenite smenu pre kreiranja Vista porudžbine."); return; }
    if (payRequired && !payment) { setPayErr(true); toast.error("Izaberite način plaćanja."); return; }
    if (scheduled && !/^\d{2}:\d{2}$/.test(schedTime)) { toast.error("Unesite zakazano vreme preuzimanja."); return; }
    setSending(true);
    try {
      const { data: order } = await api.post(`/restaurant/${restaurant.id}/internal-orders`, {
        client_order_id: clientId.current,
        payment_method: payRequired ? payment : null,
        prep_minutes: prepMinutes,
        note: note.trim() || undefined,
        scheduled_pickup_time: scheduled ? schedTime : undefined,
        items: lines.map((l) => (l.quick_selection_id
          ? { quick_selection_id: l.quick_selection_id, quantity: l.qty, person_label: l.person, note: l.note }
          : { product_id: l.product_id, quantity: l.qty, selected_options: l.ids, person_label: l.person, note: l.note })),
      });
      toast.success(scheduled ? `Zakazano za ${schedTime}` : "Poslato u pripremu");
      setLines([]); setPayment(null); setNote("");
      setScheduled(false); setSchedTime("");
      setPrepMinutes(defaultPrep);
      setLast({ id: order.id, public_code: order.public_code, total: order.total, items: order.items,
                note: order.note, payment_method: order.payment_method,
                order_source: order.order_source, created_at: order.created_at,
                pickup_time: order.confirmed_pickup_time || order.pickup_time, pickup_type: order.pickup_type });
      clientId.current = newKey();
      await doPrint(order);
      setSending(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "object" ? d.message : d) || "Greška pri kreiranju porudžbine");
      clientId.current = newKey();
      setSending(false);
    }
  };

  if (vistaLocked) {
    return (
      <div data-testid="internal-order-page">
        <h1 className="font-display font-black text-2xl mb-4">Vista</h1>
        <FeatureLocked feature="VISTA" title="Vista">
          <p className="text-xs text-slate-400 mt-3">
            Postojeće Vista porudžbine, računi i istorija smena se čuvaju.
          </p>
        </FeatureLocked>
      </div>
    );
  }

  return (
    <div data-testid="internal-order-page" className="lg:h-[calc(100vh-120px)] flex flex-col">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="font-display font-black text-2xl">Vista</h1>
        <ShiftControl rid={restaurant.id} variant="vista" shiftState={shiftState} canViewFinancials={canConfigure} />
        <PrepTimeControl minutes={prepMinutes} onChange={setPrepMinutes} />
        <ScheduleControl scheduled={scheduled} time={schedTime} prepMinutes={prepMinutes}
          onToggle={(v) => { setScheduled(v); if (v && !schedTime) {
            // Restaurant-local default, snapped to the 5-minute grid and clamped inside the same day.
            const nowMin = localMinutesOfDay();
            const target = Math.ceil((nowMin + prepMinutes + 30) / 5) * 5;
            setSchedTime(shiftHHMM("00:00", Math.min(target, 23 * 60 + 55)));
          } }}
          onTime={setSchedTime} />
        {last && (
          <div data-testid="io-last-order" className="flex items-center gap-2 rounded-xl bg-brand-green/10 px-3 py-2">
            <span className="text-sm font-bold text-brand-green">
              {last.pickup_type === "internal_scheduled" ? `Zakazano za ${last.pickup_time}` : "Poslato u pripremu"} · ŠIFRA {last.public_code}
            </span>
            {canPrintSlips() && (
            <button data-testid="io-reprint" onClick={async () => {
              if (!last?.id) return doPrint(last);
              try { await reprintOrder(api, last.id); } catch { toast.error("Štampanje nije uspelo. Pokušajte ponovo."); }
            }}
              className="flex items-center gap-1 rounded-lg bg-white border-2 border-brand-green/40 px-3 py-1.5 text-xs font-black text-brand-green">
              <Printer size={13} />Odštampaj slip
            </button>
            )}
          </div>
        )}
      </div>
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[180px_1fr_340px] gap-4 min-h-0">
        <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-y-auto pb-1">
          {(menu.categories || []).map((c) => (
            <button key={c.id} data-testid={`io-cat-${c.id}`} onClick={() => setCat(c.id)}
              className={`whitespace-nowrap rounded-xl px-4 py-3 text-sm font-bold border-2 ${cat === c.id ? "border-brand-red bg-brand-red hover:bg-brand-redHover text-white" : "border-slate-200 bg-white text-slate-600"}`}>
              {c.name}
              {c.schedule_status?.enabled && (
                <span data-testid={`io-cat-window-${c.id}`} className={`block text-[10px] font-black uppercase tracking-wider ${cat === c.id ? "text-white/80" : c.schedule_status.open_now ? "text-brand-green" : "text-slate-400"}`}>
                  {c.schedule_status.open_now ? c.schedule_status.today || "Dostupno sada" : "Van termina"}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-black uppercase tracking-wider text-slate-400">Brzi izbor</p>
            {canConfigure && (
              <button data-testid="quick-configure" onClick={() => setQuickCfg(true)}
                className="text-xs font-bold text-brand-red">Podesi</button>
            )}
          </div>
          {quick.length === 0 ? (
            <p data-testid="quick-empty" className="text-sm text-slate-400 mb-5">
              Nema podešenih brzih izbora.{canConfigure ? " Dodajte ih preko „Podesi“." : ""}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
              {quick.map((q) => (
                <button key={q.id} data-testid={`io-quick-${q.id}`} disabled={!q.available} onClick={() => addQuick(q)}
                  className="rounded-xl bg-white border-2 border-brand-red/30 px-3 py-4 text-left active:scale-[0.98] transition-transform disabled:opacity-50">
                  <span className="block font-bold text-sm leading-tight">{q.product_name}</span>
                  {(q.option_names || []).map((n, i) => <span key={i} className="block text-xs text-slate-500">{n}</span>)}
                  <span className="block font-display font-black text-sm mt-1">{q.available ? RSD(q.quick_price) : q.needs_update ? "Potrebno ažurirati" : "Nedostupno u ovom terminu"}</span>
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {shown.map((p) => {
              const outOfWindow = closedCats.has(p.category_id);
              return (
                <button key={p.id} data-testid={`io-product-${p.id}`} disabled={outOfWindow}
                  onClick={() => openProduct(p)}
                  className={`rounded-xl border-2 px-3 py-5 text-left font-bold text-base leading-tight active:scale-[0.98] transition-transform ${outOfWindow ? "bg-slate-50 border-slate-200 text-slate-400" : "bg-white border-slate-200"}`}>
                  {p.name}
                  <span className="block text-sm text-slate-400 font-semibold">
                    {outOfWindow ? "Nedostupno u ovom terminu" : RSD(p.price)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border flex flex-col max-h-[70vh] lg:max-h-none" data-testid="io-cart">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <p className="font-display font-black">Porudžbina</p>
            {lines.length > 0 && (
              <button data-testid="io-clear" onClick={clearDraft} className="text-xs font-bold text-brand-red flex items-center gap-1"><Trash2 size={14} />Obriši</button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {lines.length === 0 && <p className="text-sm text-slate-400">Izaberite proizvode.</p>}
            {lines.map((l) => (
              <div key={l.key} data-testid={`io-line-${l.key}`} className="border-b pb-2 last:border-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-bold text-sm">{l.qty}× {l.name}</p>
                  <span className="font-display font-black text-sm">{RSD(l.unit * l.qty)}</span>
                </div>
                {l.person && <p className="text-xs font-bold text-brand-red">ZA: {l.person}</p>}
                {l.names.map((m, i) => <p key={i} className={`text-xs font-semibold ${COLORS[m.color] || COLORS.DEFAULT}`}>• {m.name}</p>)}
                {l.note && <p className="text-xs font-bold text-brand-orange">Napomena: {l.note}</p>}
                <div className="flex items-center gap-2 mt-1">
                  <button data-testid={`io-minus-${l.product_id}`} onClick={() => setQty(l.key, -1)} className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Minus size={14} /></button>
                  <button data-testid={`io-plus-${l.product_id}`} onClick={() => setQty(l.key, 1)} className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Plus size={14} /></button>
                  <button data-testid={`io-edit-${l.key}`} onClick={() => editLine(l)}
                    className="h-9 px-3 rounded-lg bg-slate-100 text-xs font-black flex items-center gap-1"><Pencil size={13} />Izmeni</button>
                  <button data-testid={`io-remove-${l.key}`} onClick={() => removeLine(l.key)} className="w-9 h-9 rounded-lg bg-brand-red/10 text-brand-red flex items-center justify-center"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 border-t space-y-3">
            <textarea data-testid="io-order-note" value={note} onChange={(e) => setNote(e.target.value.slice(0, 250))}
              rows={2} placeholder="Napomena za porudžbinu (opciono)" aria-label="Napomena za porudžbinu"
              className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-red resize-none" />
            {payRequired && (
              <div className={`grid grid-cols-2 gap-2 rounded-xl ${payErr ? "ring-2 ring-brand-red p-1" : ""}`} data-testid="io-payment-section">
              {allowCash && (
                <button data-testid="io-pay-cash" onClick={() => { setPayment("cash"); setPayErr(false); }}
                  className={`rounded-xl py-3 text-sm font-bold border-2 flex items-center justify-center gap-1.5 ${payment === "cash" ? "border-brand-green bg-brand-green/5 text-brand-green" : "border-slate-200"}`}><Banknote size={15} />Gotovina</button>
              )}
              {allowCard && (
                <button data-testid="io-pay-card" onClick={() => { setPayment("card"); setPayErr(false); }}
                  className={`rounded-xl py-3 text-sm font-bold border-2 flex items-center justify-center gap-1.5 ${payment === "card" ? "border-brand-green bg-brand-green/5 text-brand-green" : "border-slate-200"}`}><CreditCard size={15} />Kartica</button>
              )}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs font-black uppercase tracking-wider text-slate-400">Ukupno</span>
              <span data-testid="io-total" className="font-display font-black text-2xl">{RSD(total)}</span>
            </div>
            <button data-testid="io-submit" disabled={!lines.length || sending || !shiftState.shift} onClick={submit}
              className="w-full rounded-2xl bg-brand-red hover:bg-brand-redHover text-white py-4 font-display font-black text-lg flex items-center justify-center gap-2 disabled:opacity-40">
              <Send size={18} />{sending ? "ŠALJEM…" : scheduled ? "ZAKAŽI PORUDŽBINU" : "POŠALJI U PRIPREMU"}
            </button>
            {!shiftState.shift && (
              <p data-testid="io-shift-required" className="text-center text-sm font-bold text-brand-red">
                {lines.length
                  ? "Smena je završena. Pokrenite novu smenu da biste poslali porudžbinu."
                  : "Pokrenite smenu pre kreiranja Vista porudžbine."}
              </p>
            )}
          </div>
        </div>
      </div>

      {config && <QuickConfig product={config.product} groups={config.groups} initial={config.initial}
        onCancel={() => setConfig(null)} onAdd={addLine} />}
      {quickCfg && (
        <QuickConfigManager rid={restaurant.id} products={available} quick={quick}
          onClose={() => setQuickCfg(false)} onChanged={loadQuick} />
      )}
    </div>
  );
}
